import { useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import {
  Button,
  SettingsEmptyState,
  GuardrailsPanel,
  type GuardrailsPanelHandle,
} from '@terra/ui-design-system';
import { useSettingsTabHeaderActions } from './settingsTabHeaderContext';
import { useApplicationGuardrailsTarget } from './testing-controls/useApplicationGuardrailsTarget';

type TestingGuardrailsTabProps = {
  isReadOnly?: boolean;
};

export function TestingGuardrailsTab({
  isReadOnly,
}: TestingGuardrailsTabProps) {
  const t = useTranslations('settings.guardrails');
  const tTestingControls = useTranslations('designSystem.testingControls');
  const panelRef = useRef<GuardrailsPanelHandle>(null);
  const target = useApplicationGuardrailsTarget();
  const canManage = !isReadOnly;
  const headerActions = useMemo(
    () =>
      canManage ? (
        <Button
          size="xs"
          variant="outline"
          flexShrink={0}
          onClick={() => panelRef.current?.openCreateDialog()}
        >
          {tTestingControls('addPrefix', {
            prefix: tTestingControls('tabs.guardrails'),
          })}
        </Button>
      ) : null,
    [canManage, tTestingControls],
  );

  useSettingsTabHeaderActions(target ? headerActions : null);

  if (!target) {
    return <SettingsEmptyState>{t('noTargetDescription')}</SettingsEmptyState>;
  }

  return (
    <GuardrailsPanel ref={panelRef} target={target} canManage={canManage} />
  );
}
